import { defineStore } from 'pinia';
import useAcvDatadog from '@/composables/useAcvDatadog';

const { addRumError } = useAcvDatadog();

export const useManageJobStore = defineStore({
  id: 'manageJobStore',
  state: () => {
    return {
      selectedJob: {},
      isPending: false,
      notification: null,
      fromSlug: null,
      errorMessage: '',
      loadingMessage: '',
      associatedJobs: null,
      isBackhaul: false,
      isLoading: false, // adding an additional loading state here to avoid confusion with actual pending jobs vs isPending
    };
  },
  actions: {
    async getJobByID(id, language) {
      this.isLoading = true;

      const URL = language
        ? `/api/transport-requests/${id}?targetLanguage=${language.toUpperCase()}`
        : `/api/transport-requests/${id}`;

      const { $api } = useNuxtApp();
      let job = null;
      let error = null;
      try {
        job = await $api(URL, {
          method: 'GET',
        });
      } catch (err) {
        error = err;
      }

      if (error) {
        this.isLoading = false;
        if (error.statusCode === 403) {
          addRumError('403 error from useManageJob - getJobByID', {
            job_id: id,
            error: error,
            errorStatus: error.statusCode,
          });

          navigateTo({
            path: '/403',
            query: { src: 'restricted assigned job' },
          });
        }
      }

      this.selectedJob = job.data;

      if (this.selectedJob?.bundleUUID) {
        await this.setAssociatedJobs(this.selectedJob.bundleUUID);
      }

      this.isLoading = false;
    },
    async findSelectedJobByID(id) {
      this.loadingMessage = 'Loading Job...';
      this.getJobByID(id);
    },
    async uploadFile(bol) {
      const { $api } = useNuxtApp();
      const jobId = this.jobId;
      let job = null;
      let error = null;
      try {
        job = await $api(`/api/bol/${jobId}`, {
          method: 'POST',
          body: bol,
          contentType: 'multi',
        });
      } catch (err) {
        error = err;
      }

      this.errorMessage = '';

      if (error) {
        this.isPending = false;
        this.notification = null;

        if (error.statusCode === 500) {
          this.errorMessage = 'Unexpected server error. Please try again.';
        } else {
          this.errorMessage = error?.data?.errors[0];
        }

        throw {
          message: this.errorMessage,
          status: error.statusCode,
        };
      }

      if (job?.data?.bolUrl) {
        this.selectedJob.billOfLadingUrl = job.data.bolUrl;
      }
    },
    async setAssociatedJobs(bundleUUID) {
      if (!bundleUUID) {
        this.associatedJobs = null;
        this.isBackhaul = false;
        return;
      }
      this.isPending = true;

      const URL = `/api/transport-requests/${bundleUUID}`;

      const { $api } = useNuxtApp();
      let bundle = null;
      let error = null;
      try {
        bundle = await $api(URL, {
          method: 'GET',
        });
      } catch (err) {
        error = err;
      }

      if (error) {
        this.isPending = false;
        if (error.statusCode === 403) {
          addRumError('403 error from useManageJob - setAssociatedJobs', {
            bundle_uuid: bundleUUID,
            errorMessage: error.message,
            errorStatus: error.statusCode,
          });

          navigateTo({
            path: '/403',
            query: { src: 'restricted assigned job' },
          });
        }
      }

      if (bundle?.data?.backhaulTransportRequests?.length > 0) {
        this.isBackhaul = true;
      }

      this.associatedJobs = [
        ...bundle?.data?.transportRequests,
        ...(bundle?.data?.backhaulTransportRequests ? bundle?.data?.backhaulTransportRequests : []),
      ];

      this.isPending = false;
    },
    async deleteFile() {
      const { $api } = useNuxtApp();
      const jobId = this.jobId;
      let job = null;
      let error = null;
      try {
        job = await $api(`/api/bol/${jobId}`, {
          method: 'DELETE',
        });
      } catch (err) {
        error = err;
      }

      this.errorMessage = '';

      if (error) {
        this.isPending = false;
        this.notification = null;

        if (error.statusCode === 500) {
          this.errorMessage = 'Failed to delete BOL';
        } else {
          this.errorMessage = error?.data?.errors[0];
        }
        addRumError(this.errorMessage, {
          statusCode: error.statusCode,
          error: error,
          jobId,
        });
        throw {
          message: this.errorMessage,
          status: error.statusCode,
        };
      }

      // resetting bolURL informs <Upload> that the file has been deleted
      // this prevents a download link from being generated
      this.selectedJob.billOfLadingUrl = undefined;
    },
    async editJob(body) {
      this.isPending = true;
      this.errorMessage = '';
      this.loadingMessage = 'Updating Job...';

      const jobId = this.jobId;

      const { $api } = useNuxtApp();
      let job = null;
      let error = null;
      try {
        job = await $api(`/api/transport-requests/v2/${jobId}`, {
          method: 'PATCH',
          body: body,
        });
      } catch (err) {
        error = err;
      }

      if (error) {
        this.isPending = false;
        this.notification = null;

        if (error?.data?.errors) {
          this.errorMessage = error.data.errors[0];
        } else {
          if (error.statusCode === 500) {
            this.errorMessage = 'Failed to update job';
            // This should be removed on edit jobs refresh
          } else if (error.statusCode === 413) {
            this.errorMessage = 'File size exceeds 15MB. Please try uploading the file again.';
          }
        }

        addRumError(this.errorMessage, {
          statusCode: error.statusCode,
          error: error,
          jobId,
        });

        throw {
          message: this.errorMessage,
          status: error.statusCode,
        };
      }

      const {
        requestId: requestId,
        vehicle: { make, year, model, trim },
      } = job.data;

      this.notification = {
        variant: 'success',
        requestId,
        vehicle: `${year} ${make} ${model} ${trim}`,
        suffix: 'has been successfully submitted.',
      };
      this.selectedJob = job.data;
      this.isPending = false;
      window.scrollTo({ top: 0, behavior: 'smooth' });

      return job.data;
    },
    async cancelJob(body) {
      const { $api } = useNuxtApp();
      const jobId = this.jobId;

      this.isPending = true;
      let job = null;
      let error = null;
      try {
        job = await $api(`/api/transport-requests/cancel/${jobId}`, {
          method: 'POST',
          body: body,
        });
      } catch (err) {
        error = err;
      }

      this.handleCancelResponse(error);

      return job?.data;
    },
    async cancelOffer() {
      const { $api } = useNuxtApp();
      const jobId = this.jobId;
      this.isLoading = true;

      let error = null;
      try {
        await $api(`/api/transport-requests/rescind-offer/${jobId}`);
      } catch (err) {
        error = err;
      }

      this.handleCancelResponse(error);
    },
    handleCancelResponse(error) {
      const {
        requestId: requestId,
        vehicle: { make, year, model, trim },
      } = this.selectedJob;

      if (error) {
        this.isLoading = false;
        this.errorMessage = error.data.errors[0];
        this.notification = {
          key: requestId,
          variant: 'error',
          notificationMessageTemplate: `<span class="tw-font-bold">Order ID ${requestId}</span> ${year} ${make} ${model} ${trim} has failed to cancel`,
        };

        throw {
          message: error.message,
          status: error.statusCode,
        };
      }

      this.notification = {
        variant: 'caution',
        requestId,
        vehicle: `${year} ${make} ${model} ${trim}`,
        suffix: 'has been cancelled.',
      };

      this.clearSelectedJob();
      this.isLoading = false;
      window.scrollTo({ top: 0, behavior: 'smooth' });
    },
    clearNotification() {
      this.notification = null;
    },
    clearSelectedJob() {
      this.selectedJob = {};
    },
    setFromSlugValue(slug) {
      this.fromSlug = slug;
    },
    setSelectedJob(job) {
      this.selectedJob = job;
    },
  },

  getters: {
    requestId: (state) => state.selectedJob?.requestId,
    cancelReason: (state) => state.selectedJob?.cancelReason,
    jobId: (state) => state.selectedJob?.id,
    payout: (state) => state.selectedJob?.pricePostedLast,
    carrierComment: (state) => state.selectedJob?.carrierComment,
    status: (state) => state.selectedJob?.status,
    dateDispatched: (state) => state.selectedJob?.dateDispatched,
    billOfLadingUrl: (state) => state.selectedJob?.billOfLadingUrl,
    datePickupActual: (state) => state.selectedJob?.datePickupActual,
    dateSLALimit: (state) => state.selectedJob?.dateSLALimit,
  },
});
